import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { queryClient } from './QueryProvider';
import { changeLanguage } from '@shared/i18n';

/**
 * RTL Context for locale and direction management
 */
const RTLContext = createContext(null);

/**
 * RTL Provider component
 * Manages locale switching and direction
 */
export function RTLProvider({ children, defaultLocale = 'ar' }) {
  const [locale, setLocale] = useState(() => {
    // Check localStorage for saved preference
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('locale');
      if (saved === 'ar' || saved === 'en') {
        return saved;
      }
    }
    return defaultLocale;
  });

  const direction = locale === 'ar' ? 'rtl' : 'ltr';
  const isRTL = direction === 'rtl';

  // Track if this is the initial render
  const isInitialRender = useRef(true);

  // Update document direction and lang when locale changes
  useEffect(() => {
    document.documentElement.dir = direction;
    document.documentElement.lang = locale;
    localStorage.setItem('locale', locale);

    // Sync i18next language
    changeLanguage(locale);

    // Invalidate all React Query cache when language changes (skip initial render)
    if (!isInitialRender.current) {
      queryClient.invalidateQueries();
    }
    isInitialRender.current = false;
  }, [direction, locale]);

  const toggleLocale = () => {
    setLocale((prev) => (prev === 'ar' ? 'en' : 'ar'));
  };

  const value = {
    locale,
    setLocale,
    toggleLocale,
    direction,
    isRTL,
  };

  return <RTLContext.Provider value={value}>{children}</RTLContext.Provider>;
}

/**
 * Hook to access RTL context
 */
export function useDirection() {
  const context = useContext(RTLContext);
  if (!context) {
    throw new Error('useDirection must be used within RTLProvider');
  }
  return context;
}

/**
 * Hook to get current locale
 */
export function useLocale() {
  const { locale, setLocale } = useDirection();
  return { locale, setLocale };
}
